import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ExternalPartyAssignmentDto} from 'features/core/cc/model/dto/entity/external-party-assignment.dto';
import {XxSidecarPlaygroundService} from '../../../service/xx-sidecar-playground.service';
import {XxPersonFlatRow} from '../../../service/xx-person-external-flat-ql.service';

/**
 * Dynamic column descriptor for one TYPED external side-car field.
 *
 * `key` is the `ext_<typeFieldId>` property on a {@link XxPersonFlatRow}; the
 * unique `*ExternalTypeField` id is the flattening key so two parties' (or two
 * types') same-named fields never alias. `header` is the German display label
 * derived from the type-field id.
 *
 * @ticket TM-1000001960
 */
interface XxExternalTypedColumn {
    /** Row property key, e.g. `ext_3` */
    key: string;
    /** Column header (German label of the type field) */
    header: string;
}

/**
 * Read-side demonstration component for the Clean-Core External Side-Car
 * TYPED bounded-1:N QL flatten with TWO join parameters (TM-1000001960).
 *
 * The component renders person rows flattened with their typed external
 * side-car fields for a selected ({@link ExternalPartyAssignmentDto},
 * XxPersonType) pair. Whereas the #4 External-Flat widget binds the side-car
 * by a single `external_party_version_id` join parameter, this #5 widget
 * binds by BOTH `external_party_version_id` and `type_id` — so the same
 * external party version can carry different typed field schemas per person type
 * (TM-1000001977: version-axis split; the legacy `external_party_assignment_id`
 * scope was removed from the typed side-car).
 *
 * Mirrors {@link XxPersonExternalFlatComponent}: a self-contained, read-only
 * DISPLAY COMPONENT (NOT a routed page, NOT a WidgetFactory `ql` data
 * provider), because the `ext_<typeFieldId>` columns are RUNTIME-DYNAMIC —
 * discovered per (assignment, type) pair — and the data arrives via a
 * two-parameter bounded-external QL join the generic widget data-provider
 * cannot express. Consumes {@link XxSidecarPlaygroundService} for all data
 * access (never a direct HttpClient call).
 *
 * Flow: party selector + type selector → on both selected, discover the typed
 * field ids via `loadTypedTypeFieldIds` → build dynamic `ext_<id>` columns →
 * load the flattened rows via `loadPersonsWithExternalTyped` → render. German
 * UI with loading / empty / error states; NULL side-car values render as an
 * em dash.
 *
 * @ticket TM-1000001960
 */
@Component({
    selector: 'mvs-xx-person-external-typed',
    templateUrl: './xx-person-external-typed.component.html',
    styleUrls: ['./xx-person-external-typed.component.scss'],
    standalone: false
})
export class XxPersonExternalTypedComponent implements OnInit, OnChanges, OnDestroy {

    /** True once both selector lists have finished their first load. */
    initialized = false;

    /** Available assignments for the party selector. */
    assignments: ExternalPartyAssignmentDto[] = [];

    /** Available person types for the type selector. */
    personTypes: any[] = [];

    /** Currently selected assignment (drives the flatten query). */
    selectedAssignment: ExternalPartyAssignmentDto | null = null;

    /** Currently selected person type (drives the flatten query). */
    selectedType: any | null = null;

    /** Dynamic `ext_<typeFieldId>` columns for the selected pair. */
    externalColumns: XxExternalTypedColumn[] = [];

    /** Flattened person rows (core fields + `ext_<id>` values). */
    rows: XxPersonFlatRow[] = [];

    /** True while the flatten query / type-field lookup is running. */
    loading = false;

    /** Holds a user-facing German error message, or null when there is none. */
    errorMessage: string | null = null;

    constructor(private playgroundService: XxSidecarPlaygroundService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     *
     * @ticket TM-1000001960
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Refresh Component: (re)load both selector lists.
     *
     * @ticket TM-1000001960
     */
    refreshComponent(): void {
        this.loadSelectors();
    }

    /**
     * Process changes within Binding.
     *
     * @param changes the bound input changes
     * @ticket TM-1000001960
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     *
     * @ticket TM-1000001960
     */
    ngOnDestroy(): void {
    }

    /**
     * Load the ExternalPartyAssignments and XxPersonTypes that populate the two
     * selectors. The component is initialized once the assignment list resolves;
     * the type list loads in parallel.
     *
     * @ticket TM-1000001960
     */
    loadSelectors(): void {
        this.errorMessage = null;

        this.playgroundService.loadExternalPartyAssignments().subscribe({
            next: (assignments) => {
                this.assignments = assignments ?? [];
                this.initialized = true;
            },
            error: () => {
                this.errorMessage = 'Die externen Parteien / Zuordnungen konnten nicht geladen werden.';
                this.initialized = true;
            }
        });

        this.playgroundService.loadXxPersonTypes().subscribe({
            next: (types) => {
                this.personTypes = types ?? [];
            },
            error: () => {
                this.errorMessage = 'Die Personentypen konnten nicht geladen werden.';
            }
        });
    }

    /**
     * Selection handler: once BOTH an assignment and a person type are chosen,
     * discover the typed `ext_<id>` columns for the (version, type) pair, then
     * load the flattened rows. Type fields and rows are loaded together so the
     * table never renders columns without data.
     *
     * TM-1000001977: both discovery and read scope by the shared ExternalPartyVersion
     * stream carried on the assignment as `externalPartyVersionDtoId`.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    onSelectionChange(): void {
        this.externalColumns = [];
        this.rows = [];
        this.errorMessage = null;

        if (!this.selectedAssignment || !this.selectedType) {
            return;
        }

        // TM-1000001977: discovery AND read both scope by the shared version stream.
        const versionId = this.selectedAssignment.externalPartyVersionDtoId;
        if (versionId == null) {
            this.errorMessage = 'Für diese Zuordnung ist kein Versionsstrom (ExternalPartyVersion) hinterlegt.';
            return;
        }

        const typeId = this.selectedType.id;
        this.loading = true;

        this.playgroundService.loadTypedTypeFieldIds(versionId, typeId).subscribe({
            next: (typeFieldIds) => {
                const ids = typeFieldIds ?? [];
                this.externalColumns = this.buildColumns(ids);
                this.loadRows(versionId, typeId, ids);
            },
            error: () => {
                this.loading = false;
                this.errorMessage = 'Die typisierten externen Felddefinitionen konnten nicht geladen werden.';
            }
        });
    }

    /**
     * Build the dynamic column descriptors from the typed field ids. The column
     * key is `ext_<typeFieldId>` (the unique flattening key); the header is a
     * German label derived from the field id.
     *
     * @ticket TM-1000001960
     */
    private buildColumns(typeFieldIds: number[]): XxExternalTypedColumn[] {
        return [...typeFieldIds]
            .sort((a, b) => a - b)
            .map(id => ({
                key: `ext_${id}`,
                header: `Feld ${id}`
            }));
    }

    /**
     * Run the two-parameter (version, type) bounded-external flatten and store the rows.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    private loadRows(versionId: number, typeId: number, typeFieldIds: number[]): void {
        this.playgroundService.loadPersonsWithExternalTyped(versionId, typeId, typeFieldIds).subscribe({
            next: (rows) => {
                this.rows = rows ?? [];
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.errorMessage = 'Die Personendaten mit typisierten externen Feldern konnten nicht geladen werden.';
            }
        });
    }

    /**
     * Section title labelled by the selected party AND type (per spec §3).
     * Example: Externe Daten (typisiert) — NÜRNBERGER Privat / Antragsteller.
     *
     * @ticket TM-1000001960
     */
    get externalSectionTitle(): string {
        if (!this.selectedAssignment || !this.selectedType) {
            return 'Externe Daten (typisiert)';
        }
        const partyName = this.resolveAssignmentLabel(this.selectedAssignment);
        const typeName = this.resolveTypeLabel(this.selectedType);
        return `Externe Daten (typisiert) — ${partyName} / ${typeName}`;
    }

    /**
     * Human-readable label for an assignment. Uses the assignment name (DtoDetail)
     * and the shared ExternalPartyVersion stream name.
     *
     * TM-1000001977 Phase 3.6: externalPartyDtoName / protocol / version deleted from
     * ExternalPartyAssignment; use assignment.name + externalPartyVersionDtoName instead.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    resolveAssignmentLabel(assignment: ExternalPartyAssignmentDto): string {
        const parts: string[] = [];
        parts.push(assignment.name ?? `Zuordnung ${assignment.id}`);
        if (assignment.externalPartyVersionDtoName) {
            parts.push(`(Version: ${assignment.externalPartyVersionDtoName})`);
        }
        return parts.join(' ');
    }

    /**
     * Human-readable label for a person type: prefer `name`, fall back to
     * `description`, then the id.
     *
     * @ticket TM-1000001960
     */
    resolveTypeLabel(type: any): string {
        if (!type) {
            return '';
        }
        if (type.name) {
            return String(type.name);
        }
        if (type.description) {
            return String(type.description);
        }
        return `Typ ${type.id}`;
    }

    /**
     * Render a flattened external value for the table cell. NULL / undefined
     * side-car values render as an em dash so absent fields are visible.
     *
     * @ticket TM-1000001960
     */
    displayValue(row: XxPersonFlatRow, key: string): string {
        const value = row[key];
        if (value === null || value === undefined || value === '') {
            return '—';
        }
        return String(value);
    }
}
